import React, { useCallback } from 'react';
import { InputNumber, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLiquidVolumeUnits } from '../../hooks/useSprayCalculatorData';
import { HtmlContent } from '@client/utils/html_content';

/**
 * Step 1 - Sprayer Component
 * Simple form for tank volume and unit selection
 *
 * @param {Object} props
 * @param {Object} props.selections - Current selections from useSprayCalculator
 * @param {Function} props.setTankVolume - Setter for tank volume
 * @param {Function} props.setTankVolumeUnitId - Setter for tank volume unit (accepts unitId, unitName)
 * @param {Object} props.enrichedData - Enriched data with unit labels
 */
const Step1Sprayer = React.memo(
  ({ selections, setTankVolume, setTankVolumeUnitId, enrichedData }) => {
    const { t } = useTranslation('sprayCalculator');
    const { data: liquidVolumeUnits = [], isLoading: isLoadingUnits } =
      useLiquidVolumeUnits();

    const handleVolumeChange = useCallback(
      (value) => {
        setTankVolume(value);
      },
      [setTankVolume]
    );

    const handleUnitChange = useCallback(
      (unitId, option) => {
        setTankVolumeUnitId(unitId, option?.rawLabel || null);
      },
      [setTankVolumeUnitId]
    );

    const unitOptions = liquidVolumeUnits.map((unit) => ({
      value: unit.id,
      label: <HtmlContent html={unit.name} />,
      rawLabel: unit.name,
    }));

    return (
      <div className="flex flex-col gap-4 sm:gap-6 w-full">
        <h3 className="text-xl sm:text-2xl lg:text-[30px] font-medium text-center normal-case">
          {t('sprayer_title')}
        </h3>
        <p className="text-xs sm:text-sm text-[#000]">
          {t('sprayer_description')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full p-3 sm:p-4 lg:p-6 rounded-2xl bg-[#FAFAFA]">
          {/* Tank volume input */}
          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('sprayer_volume_label')}
            </label>
            <InputNumber
              value={
                selections.tankVolume ? parseFloat(selections.tankVolume) : null
              }
              onChange={handleVolumeChange}
              placeholder={t('sprayer_volume_placeholder')}
              min={0}
              className="w-full"
              size="large"
            />
          </div>

          {/* Unit select */}
          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('sprayer_unit_label')}
            </label>
            <Select
              showSearch
              filterOption={false}
              value={selections.tankVolumeUnitId || undefined}
              onChange={handleUnitChange}
              placeholder={t('sprayer_unit_placeholder')}
              options={unitOptions}
              loading={isLoadingUnits}
              className="w-full"
              size="large"
            />
          </div>
        </div>
      </div>
    );
  }
);

Step1Sprayer.displayName = 'Step1Sprayer';

export default Step1Sprayer;
